<template>
  <div class="the-header-state-switch">
    <LcButton
      :aria-label="licensedTerritoryAriaLabel"
      :class="{
        'px-100': !iconOnly,
        'the-header-state-switch-button': iconOnly,
      }"
      icon="language-lt"
      size="small"
      type="button"
      variant="raised"
      @click="isStateSwitchModalVisible = true"
    >
      <template v-if="!iconOnly">
        {{ licensedTerritory.replace('US-', '') }}
      </template>
    </LcButton>

    <ClientOnly>
      <TheHeaderStateSwitchModal v-model:is-visible="isStateSwitchModalVisible" />
      <TheHeaderStateSwitchRedirectModal v-model:is-visible="isStateSwitchRedirectModalVisible" />
    </ClientOnly>
  </div>
</template>

<script lang="ts" setup>
import {
  REDIRECT_COOKIE_KEY,
  RedirectType,
  stateSwitchQueryParam,
  stateSwitchRedirectResultQueryParam,
  stateSwitchRedirectTypeQueryParam,
} from '~/components/TheHeader/types'

const { iconOnly = false } = defineProps<{ iconOnly?: boolean }>()

const { t } = useI18n()
const { addToast } = useToaster()
const { query } = useRoute()
const { removeQueryParams } = useRouterUtils()
const { isStateSwitchRedirectModalVisible, licensedTerritory } = useStateSwitch()

const isStateSwitchModalVisible = ref(false)

onMounted(() => {
  let queryParams = []

  if (query[stateSwitchQueryParam]) {
    addToast({
      headline: t('stateswitch.toast.headline', {
        state: t(`licensedterritory.${licensedTerritory}`),
      }),
      message: t('stateswitch.toast.message'),
      tracking: {
        action: 'state_switch',
        topicKey: 'state_switch_success',
      },
      type: 'info',
    })

    queryParams.push(stateSwitchQueryParam)
  }

  if (query[stateSwitchRedirectTypeQueryParam]) {
    const cookie = useCookie<{
      isStateSwitchRedirectModalVisible: Maybe<boolean>
      redirectResult: Maybe<string>
    }>(REDIRECT_COOKIE_KEY, {
      default: () => ({
        isStateSwitchRedirectModalVisible: null,
        redirectResult: null,
      }),
    })

    queryParams = [
      stateSwitchRedirectResultQueryParam,
      stateSwitchRedirectTypeQueryParam,
      ...queryParams,
    ]

    if (query[stateSwitchRedirectTypeQueryParam] === RedirectType.Cloudflare) {
      isStateSwitchRedirectModalVisible.value =
        cookie.value.isStateSwitchRedirectModalVisible ?? true
      cookie.value = {
        isStateSwitchRedirectModalVisible: false,
        redirectResult: String(query[stateSwitchRedirectResultQueryParam]),
      }
    }
  }

  if (!queryParams.length) return
  removeQueryParams(...queryParams)
})

const licensedTerritoryAriaLabel = computed(() =>
  t(`licensedterritory.${licensedTerritory.replace('-', '').toLocaleLowerCase()}`),
)
</script>

<style lang="scss" scoped>
.the-header-state-switch {
  line-height: 1;
}

.the-header-state-switch-button {
  padding: token('size/space/gap/050');
  border: none;

  :deep(i) {
    margin: 0;
  }
}
</style>
